import { Skeleton } from "@/components/ui/skeleton"
import { HelpdeskLink } from "@/features/helpdesk"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable"
import { EyeOff } from "lucide-react"
import { useState } from "react"
import type { StoreApi, UseBoundStore } from "zustand"
import { CustomizeTray } from "./CustomizeTray"
import { SortableWidget, type DashboardWidgetDef } from "./DashboardBoardShell"
import { WidgetCard } from "./WidgetCard"
import {
  DashboardModeContext,
  localeForLanguage,
  type DashboardBoardState,
} from "./dashboardStore"

export interface DashboardBoardProps<K extends string, Stats> {
  titleKey: TranslationKeys
  /** Studio-specific header buttons (e.g. customer studio's Generate/Import
   * actions) rendered before the %/# toggle. */
  headerActions?: React.ReactNode
  /** The article that explains this dashboard, linked under the title. */
  helpArticle?: { categoryId: string; articleId: string }
  useStore: UseBoundStore<StoreApi<DashboardBoardState<K>>>
  widgets: Record<K, DashboardWidgetDef<Stats>>
  stats: Stats
  isLoading: boolean
  isError: boolean
  refetch: () => void
  /** Agent-addressable target key (see `agentTargets.ts`), marked on the canvas root. */
  agentTarget?: string
}

/**
 * Full dashboard page shell — canvas background, header, loading/error
 * states, drag-sortable widget grid, and the customize tray. Extracted from
 * the supplier and customer Studio dashboard pages, which were byte-identical
 * apart from the title, header actions, and which store/registry/stats they
 * fed in.
 */
export function DashboardBoard<K extends string, Stats>({
  titleKey,
  headerActions,
  helpArticle,
  useStore,
  widgets,
  stats,
  isLoading,
  isError,
  refetch,
  agentTarget,
}: DashboardBoardProps<K, Stats>) {
  const { translate, currentLanguage } = useTranslation()
  const locale = localeForLanguage(currentLanguage)
  const mode = useStore((s) => s.mode)
  const setMode = useStore((s) => s.setMode)
  const order = useStore((s) => s.order)
  const hidden = useStore((s) => s.hidden)
  const moveWidget = useStore((s) => s.moveWidget)
  const hideWidget = useStore((s) => s.hideWidget)
  const showWidget = useStore((s) => s.showWidget)
  const [editing, setEditing] = useState(false)
  const [activeKey, setActiveKey] = useState<K | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const visibleOrder = order.filter((key) => !hidden.includes(key))
  const activeDef = activeKey ? widgets[activeKey] : null
  const ActiveWidget = activeDef?.component

  return (
    // DashboardModeContext provides the %/# toggle to DualValue inside every
    // widget, sourced from this page's own store instance.
    <DashboardModeContext.Provider value={mode}>
      {/* Full-bleed canvas: pull back the studio layout's px-6 pb-6 pt-4
          frame so the chat-style backdrop reaches the header and sidebar
          edges. */}
      <div
        data-agent-target={agentTarget}
        className="relative -mx-6 -mb-6 -mt-4 h-[calc(100%+2.5rem)] overflow-hidden bg-background"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.14)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.10)_1px,transparent_1px)] bg-size-[72px_72px]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-linear-to-b from-grey1/55 via-grey2/15 to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_55%_at_50%_-10%,hsl(var(--primary)/0.07),transparent_70%)]"
        />
        <div className="relative z-10 h-full overflow-auto">
          <div className="mx-auto max-w-[1264px] px-10 pb-16 pt-9">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-7">
              <div>
                <h1 className="text-[25px] text-white">
                  {translate(titleKey)}
                </h1>
                {helpArticle ? (
                  <HelpdeskLink {...helpArticle} className="mt-1.5" />
                ) : null}
              </div>
              <div className="flex items-center gap-3">
                {headerActions}
                <div
                  role="group"
                  aria-label={translate("MC_SSD_TOGGLE_ARIA")}
                  className="inline-flex gap-0.5 rounded-full border border-grey4 bg-grey2 p-[3px]"
                >
                  {(["pct", "abs"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      aria-pressed={mode === m}
                      onClick={() => setMode(m)}
                      className={cn(
                        "h-7 min-w-9 rounded-full px-3 text-[13px] font-semibold text-grey5",
                        mode === m && "bg-grey1 text-white",
                      )}
                    >
                      {m === "pct" ? "%" : "#"}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  aria-pressed={editing}
                  onClick={() => setEditing((prev) => !prev)}
                  className={cn(
                    "h-8 rounded-full border border-grey4 px-3.5 text-[13px] font-semibold text-grey5 transition-colors hover:text-white",
                    editing && "bg-grey1 text-white",
                  )}
                >
                  {editing
                    ? translate("MC_SSD_DONE")
                    : translate("MC_SSD_CUSTOMIZE")}
                </button>
              </div>
            </div>
            {isLoading ? (
              <div className="grid grid-cols-1 items-start gap-[18px] xl:grid-cols-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    className={cn(
                      "h-56 rounded-xl",
                      i === 0 && "col-span-full",
                    )}
                  />
                ))}
              </div>
            ) : (
              <>
                {isError && (
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red1/40 bg-red1/10 px-4 py-2.5 text-[13px] text-red1">
                    <span>{translate("MC_SSD_LOAD_ERROR")}</span>
                    <button
                      type="button"
                      onClick={() => refetch()}
                      className="shrink-0 rounded-full border border-red1/40 px-3 py-1 text-[12px] font-semibold text-red1 transition-colors hover:bg-red1/10"
                    >
                      {translate("MC_SSD_RETRY")}
                    </button>
                  </div>
                )}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={({ active }) => setActiveKey(active.id as K)}
                  onDragEnd={({ active, over }) => {
                    if (over && active.id !== over.id) {
                      moveWidget(active.id as K, order.indexOf(over.id as K))
                    }
                    setActiveKey(null)
                  }}
                  onDragCancel={() => setActiveKey(null)}
                >
                  {(() => {
                    // A CSS grid enforces a shared row height across each row, so
                    // pairing a short widget with a taller one leaves dead space
                    // under the short one. Split narrow widgets into two
                    // independent flex-col stacks (even-index → left,
                    // odd-index → right) so each column packs tight top-to-bottom.
                    // Wide widgets stay full-bleed above.
                    const wideOrder = visibleOrder.filter(
                      (k) => widgets[k].wide,
                    )
                    const narrowOrder = visibleOrder.filter(
                      (k) => !widgets[k].wide,
                    )
                    const leftOrder = narrowOrder.filter((_, i) => i % 2 === 0)
                    const rightOrder = narrowOrder.filter((_, i) => i % 2 === 1)
                    // `items` must match DOM render order or
                    // rectSortingStrategy's collision detection reorders against
                    // the wrong index.
                    const domOrder = [...wideOrder, ...leftOrder, ...rightOrder]
                    const renderItem = (key: K) => {
                      const def = widgets[key]
                      const Widget = def.component
                      return (
                        <SortableWidget
                          key={key}
                          id={key}
                          disabled={!editing}
                          wide={def.wide}
                        >
                          {({ isDragging, isOver, dragHandleProps }) => (
                            <WidgetCard
                              titleKey={def.titleKey}
                              wide={def.wide}
                              editing={editing}
                              dragHandleProps={dragHandleProps}
                              linkTo={editing ? undefined : def.linkTo}
                              linkLabelKey={
                                editing ? undefined : def.linkLabelKey
                              }
                              countText={def.countText?.(
                                stats,
                                translate,
                                locale,
                              )}
                              className={cn(
                                isDragging && "opacity-40",
                                isOver &&
                                  "border-yellow1 ring-2 ring-yellow1/15",
                              )}
                              headerExtra={
                                editing ? (
                                  <button
                                    type="button"
                                    onClick={() => hideWidget(key)}
                                    aria-label={`${translate("MC_SSD_HIDE")}: ${translate(def.titleKey)}`}
                                    className="inline-flex items-center gap-1 rounded-sm text-xs font-medium text-grey5 transition-colors duration-150 hover:text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                                  >
                                    <EyeOff size={12} />
                                    {translate("MC_SSD_HIDE")}
                                  </button>
                                ) : undefined
                              }
                            >
                              <Widget stats={stats} loadFailed={isError} />
                            </WidgetCard>
                          )}
                        </SortableWidget>
                      )
                    }

                    return (
                      <SortableContext
                        items={domOrder}
                        strategy={rectSortingStrategy}
                      >
                        <div className="flex flex-col gap-[18px]">
                          {wideOrder.length > 0 && (
                            <div className="flex flex-col gap-[18px]">
                              {wideOrder.map(renderItem)}
                            </div>
                          )}
                          <div className="grid grid-cols-1 items-start gap-[18px] xl:grid-cols-2">
                            <div className="flex flex-col gap-[18px]">
                              {leftOrder.map(renderItem)}
                            </div>
                            <div className="flex flex-col gap-[18px]">
                              {rightOrder.map(renderItem)}
                            </div>
                          </div>
                        </div>
                      </SortableContext>
                    )
                  })()}
                  <DragOverlay>
                    {activeDef && ActiveWidget ? (
                      <WidgetCard
                        titleKey={activeDef.titleKey}
                        wide={activeDef.wide}
                        countText={activeDef.countText?.(
                          stats,
                          translate,
                          locale,
                        )}
                        className="scale-[1.02] cursor-grabbing border-yellow1/50 shadow-2xl shadow-black/40"
                      >
                        <ActiveWidget stats={stats} loadFailed={isError} />
                      </WidgetCard>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              </>
            )}
            {editing && (
              <CustomizeTray
                hidden={hidden}
                onShow={showWidget}
                widgets={widgets}
                titleKey="MC_SSD_TRAY_TITLE"
                emptyKey="MC_SSD_TRAY_EMPTY"
              />
            )}
          </div>
        </div>
      </div>
    </DashboardModeContext.Provider>
  )
}
